/**
 * Cliente HTTP del ledger de créditos, compartido por las apps de la familia.
 *
 * Pega contra `GET /api/me/credits/transactions` (ya existente en
 * `apps/backend/src/routes/billing.ts`) — devuelve los movimientos crudos del
 * ledger (grants, debits, refunds, adjusts) en shape plano `{ items, limit }`.
 *
 * Cada app decide qué subset mostrar (speak filtra los débitos con sesión
 * porque sus consumos vienen por la lista de sesiones; fit ídem con las
 * suyas). El filtrado/merge vive en el `walletModel` de cada app.
 *
 * Montos en nano-USD (1e8 = $1) como string BigInt-safe.
 */

import { apiFetch } from '@orborix/auth-client';

export type LedgerKind = 'grant' | 'debit' | 'refund' | 'adjust';

export interface LedgerTxn {
  id: string;
  kind: LedgerKind;
  /** nano-USD con signo (positivo en grant/refund, negativo en debit). */
  amountNano: string;
  /** Link a la sesión que originó el débito (null en recargas/bonos manuales). */
  sessionId: string | null;
  /** Tipo de servicio del débito (`llm`/`tts`/.../`listen_unlock`), null en grants. */
  callKind: string | null;
  /** Nota libre del backend (método de pago, motivo del bono, etc.). */
  notes: string | null;
  createdAt: string;
}

interface TransactionsResponse {
  items: Array<{
    id: string;
    kind: LedgerKind;
    amountNano: string;
    sessionId: string | null;
    callKind: string | null;
    notes: string | null;
    createdAt: string;
  }>;
}

export class WalletApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'WalletApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Trae los últimos `limit` movimientos del ledger. `signal` permite cancelar al
 * desmontar. Devolvemos el subconjunto de campos que los wallets necesitan.
 */
export async function fetchLedgerTransactions(
  limit = 50,
  signal?: AbortSignal,
): Promise<LedgerTxn[]> {
  let res: Response;
  try {
    res = await apiFetch(`/api/me/credits/transactions?limit=${encodeURIComponent(String(limit))}`, {
      ...(signal ? { signal } : {}),
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new WalletApiError('No pudimos conectar con el servidor.', 0, 'network_error');
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new WalletApiError(body.error ?? `HTTP ${res.status}`, res.status, body.error ?? 'unknown');
  }
  const json = (await res.json()) as TransactionsResponse;
  return json.items.map((r) => ({
    id: r.id,
    kind: r.kind,
    amountNano: r.amountNano,
    sessionId: r.sessionId,
    callKind: r.callKind,
    notes: r.notes,
    createdAt: r.createdAt,
  }));
}
